class CustomerPhotos extends HTMLElement {
    #initialized = false
    #mounted = false
    #photos = []
    #initPromise = null

    constructor() {
        super();
        this.productId = this.getAttribute('data-product-id');
        this.appKey = this.getAttribute('data-app-key');
        this.button = this.querySelector('#customer-photos-btn');
        this.modal = document.querySelector(this.querySelector('modal-opener')?.getAttribute('data-modal'));
        this.gallery = this.modal?.querySelector('#photos-gallery');
    }
    connectedCallback() {
        if (this.button) {
            this.button.addEventListener('click', async () => {
                if (!this.#initialized)
                    await this.init();
                await this.loadStylesheet();
                //mount media to modal here
                if (!this.#mounted) {
                    this.mountPhotos();
                    await this.initSwipers();
                } else if (this.mainSwiper && this.thumbSwiper) {
                    this.mainSwiper.update();
                    this.thumbSwiper.update();
                }
                this.modal?.classList.remove('hidden');
            });
        }
        if (window.luxoRunWhenIdle) window.luxoRunWhenIdle(() => this.init(), 5000);
        else if ('requestIdleCallback' in window) requestIdleCallback(() => this.init(), { timeout: 5000 });
        else setTimeout(() => this.init(), 5000);
    }
    async init() {
        if (this.#initPromise) return this.#initPromise;

        this.#initPromise = this.fetchCustomerPhotos().then(async (photos) => {
            this.updatePhotoCount(photos.length);
            if (photos.length > 0) {
                await this.loadStylesheet();
                this.classList.remove('hidden');
            }
            this.#initialized = true;
            return photos;
        });

        return this.#initPromise;
    }

    async fetchCustomerPhotos() {
        if (this.#photos.length > 0) {
            return this.#photos; // use cache
        }
        try {
            const response = await fetch(
                `https://api-cdn.yotpo.com/v3/storefront/store/${this.appKey}/product/${this.productId}/reviews?perPage=150&sort=images`
            );
            const data = await response.json();
            const photos = [];
            data.reviews.filter(review => review.imagesData && review.imagesData.length > 0)
                .forEach(review => {
                    photos.push(...review.imagesData.map(img => { return { originalUrl: img.originalUrl, thumbUrl: img.thumbUrl }; }));
                });

            this.#photos = photos; // cache
            return photos;
        } catch (err) {
            console.error('Error fetching customer photos:', err);
            return [];
        }
    }

    loadStylesheet() {
        const stylesheetUrl = this.dataset.stylesheetUrl;
        if (!stylesheetUrl) return Promise.resolve();
        if (window.luxoLoadStyleOnce) return window.luxoLoadStyleOnce(stylesheetUrl);

        const stylesheetHref = new URL(stylesheetUrl, window.location.href).href;
        const existingStylesheet = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
            (link) => link.href === stylesheetHref
        );
        if (existingStylesheet?.sheet) return Promise.resolve(existingStylesheet);

        return new Promise((resolve) => {
            const stylesheet = existingStylesheet || document.createElement('link');
            const markReady = () => resolve(stylesheet);

            stylesheet.addEventListener('load', markReady, { once: true });
            stylesheet.addEventListener('error', markReady, { once: true });

            if (!existingStylesheet) {
                stylesheet.rel = 'stylesheet';
                stylesheet.href = stylesheetUrl;
                document.head.appendChild(stylesheet);
            }
        });
    }

    updatePhotoCount(count) {
        const countEl = document.querySelectorAll('[data-customer-photos-count]');
        if (countEl) countEl.forEach(el => el.textContent = count);
    }

    mountPhotos() {
        if (!this.gallery) return;
        this.gallery.innerHTML = `
            <div class="swiper-container main-swiper">
                <div class="swiper-pagination"></div>
                <div class='swiper-wrapper'>
                    ${this.#photos.map(url => `
                        <div class="image-wrapper swiper-slide">
                            <div class="loading__spinner">
                                <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    class="spinner"
                                    viewBox="0 0 66 66"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                                </svg>
                            </div>
                            <img src="${url.originalUrl}" alt="Gallery Image" loading="lazy"
                                srcset="${url.originalUrl}&width=300 300w, ${url.originalUrl}&width=600 600w, ${url.originalUrl}&width=1200 1200w"
                                sizes="(max-width: 600px) 100vw, 300px" onload="this.previousElementSibling.style.display='none';" >
                        </div>
                        `).join('')}
                </div>
                <div class="swiper-button-next"></div>
                <div class="swiper-button-prev"></div>
            </div>
            <div class="swiper-container thumb-swiper">
                <div class='swiper-wrapper'>
                    ${this.#photos.map(url => `
                        <div class="image-wrapper swiper-slide">
                            <div class="loading__spinner">
                                <svg
                                    aria-hidden="true"
                                    focusable="false"
                                    class="spinner"
                                    viewBox="0 0 66 66"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                                </svg>
                            </div>
                            <img src="${url.thumbUrl}" alt="Gallery Image" loading="lazy"
                                srcset="${url.thumbUrl}&width=300 300w, ${url.thumbUrl} 600w, ${url.thumbUrl} 1200w"
                                sizes="(max-width: 600px) 100vw, 300px" onload="this.previousElementSibling.style.display='none';" >
                        </div>
                        `).join('')}
                </div>
            </div>
            `;
        this.#mounted = true
    }
    async initSwipers() {
        await window.luxoLoadSwiper();
        this.thumbSwiper = new Swiper('.thumb-swiper', {
            spaceBetween: 10,
            slidesPerView: "auto",
            freeMode: true,
            watchSlidesProgress: true,
        });
        this.mainSwiper = new Swiper('.main-swiper', {
            spaceBetween: 10,
            navigation: {
                nextEl: ".main-swiper .swiper-button-next",
                prevEl: ".main-swiper .swiper-button-prev",
            },
            pagination: {
                el: ".main-swiper .swiper-pagination",
                type: "fraction",
            },
            thumbs: {
                swiper: this.thumbSwiper
            }
        });
    }
}

if (!customElements.get('customer-photos')) {
    customElements.define('customer-photos', CustomerPhotos);
}
